/**
 * attribution.js — first-party campaign attribution.
 *
 * WHY THIS EXISTS
 * Shopify's own `Order.customerJourneySummary` works on this store (measured
 * 2026-08-13, Basic plan, `ready: true`) but it is channel-level only: the
 * source reads "direct" or "Google", the landing page is the homepage on every
 * order sampled, and `utmParameters` is null. It can never answer "which blog
 * post produced this sale". This script answers that by stamping the visitor's
 * first and last campaign touch onto the cart, where it rides through checkout
 * and lands on the order as `customAttributes`.
 *
 * WHAT IT WRITES (all keys underscore-prefixed, so Shopify hides them from the
 * customer's order confirmation, invoices and the storefront cart):
 *   _up_first  source|medium|campaign|content|YYYY-MM-DD   never overwritten
 *   _up_last   same shape                                  latest known touch
 *   _up_path   up to 5 internal touches, ">"-joined        the content trail
 *   _up_funnel stage:MMDD>stage:MMDD                       how far they got
 *
 * THE FUNNEL FIELD
 * `_up_path` says what they read; `_up_funnel` says how far they got. Stages are
 * ordered and only ever advance — land → browse → product → cart → checkout —
 * each stamped with the day it was first reached. Together with `_up_first` this
 * answers the question the channel reports cannot: *which campaign's traffic
 * stalls at which step*.
 *
 * The drop-off half of that answer needs the attributes to exist on records that
 * are not orders. Two carry them:
 *   - AbandonedCheckout.customAttributes — verified readable via the Admin API,
 *     so a visitor who reaches checkout and leaves is attributable by campaign.
 *   - The cart itself, for anyone who stops earlier.
 * Reaching checkout is therefore stamped with sendBeacon rather than fetch: the
 * page is navigating away at that moment and a normal fetch is cancelled, which
 * would lose exactly the stage we most want recorded.
 *
 * DESIGN NOTES
 * - Storage is first-party localStorage. No third-party cookie, no network
 *   call to anyone but this shop.
 * - We only touch the cart once it holds something. Calling /cart/update.js on
 *   every pageview would mint a cart token for every passer-by; stamping after
 *   the first add keeps that noise out of Shopify's own reporting.
 * - Last touch only advances on an *identifiable* touch. Plain internal
 *   navigation must not overwrite a campaign with "direct" — that is the
 *   classic way attribution data quietly turns into mush.
 * - Every storage call is wrapped: Safari private mode throws on setItem, and
 *   an analytics helper must never break add-to-cart.
 */
(function () {
  'use strict';

  var STORE_KEY = 'up_attr_v1';
  var SENT_KEY = 'up_attr_sent_v1';
  var TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  var PATH_MAX = 5;
  var FIELD_MAX = 120; // keep each attribute comfortably inside Shopify's limit

  // Ordered funnel stages. Index is the rank: a stage never moves backwards, so
  // a shopper who reaches checkout and then browses more stays at 'checkout'.
  var STAGES = ['land', 'browse', 'product', 'cart', 'checkout'];

  var cfg = window.upAttribution || {};

  // ── storage helpers ───────────────────────────────────────────────────────
  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.firstAt) return null;
      if (Date.now() - data.firstAt > TTL_MS) {
        window.localStorage.removeItem(STORE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeStore(data) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode, quota — attribution is best-effort by design */
    }
  }

  // ── consent ───────────────────────────────────────────────────────────────
  // If the shop has a consent banner configured, honour it. If the privacy API
  // is absent no banner is in play, and this first-party record proceeds.
  //
  // The API loads asynchronously, so "not there yet" is not the same as "no
  // banner": answering at boot would let a page with an undecided banner record
  // anyway. consentDecided() separates the two, and visitorConsentCollected
  // re-runs the check when the shopper actually answers.
  function consentOk() {
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      if (!p || typeof p.analyticsProcessingAllowed !== 'function') return true;
      return p.analyticsProcessingAllowed() !== false;
    } catch (e) {
      return true;
    }
  }

  function consentApiReady() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    return !!(p && typeof p.analyticsProcessingAllowed === 'function');
  }

  // ── reading the current touch ─────────────────────────────────────────────
  function clean(value) {
    if (!value) return '';
    return String(value)
      .replace(/[|>]/g, '-') // our own separators
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, FIELD_MAX);
  }

  function today() {
    var d = new Date();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }

  function referrerSource() {
    try {
      if (!document.referrer) return '';
      var host = new URL(document.referrer).hostname;
      if (!host || host === window.location.hostname) return '';
      return host.replace(/^www\./, '');
    } catch (e) {
      return '';
    }
  }

  /**
   * The touch this pageview represents, or null when there is nothing worth
   * recording. Precedence: explicit UTM > short ?c= code > external referrer.
   */
  function currentTouch() {
    var q = new URLSearchParams(window.location.search);
    var campaign = clean(q.get('utm_campaign') || q.get('c'));
    var source = clean(q.get('utm_source'));
    var medium = clean(q.get('utm_medium'));
    var content = clean(q.get('utm_content'));

    if (campaign || source) {
      return {
        source: source || 'unknown',
        medium: medium || (campaign ? 'campaign' : 'referral'),
        campaign: campaign || 'untagged',
        content: content,
        date: today()
      };
    }

    var ref = referrerSource();
    if (ref) {
      return { source: ref, medium: 'referral', campaign: '', content: '', date: today() };
    }

    return null;
  }

  /**
   * The internal content touch — which of our own pages the visitor is on.
   * This is what makes per-article revenue answerable; a blog reader who buys
   * three days later still carries `article:<handle>` in the path.
   */
  function internalTouch() {
    if (cfg.pageType === 'article' && cfg.handle) return 'article:' + clean(cfg.handle);
    if (cfg.pageType === 'blog' && cfg.handle) return 'blog:' + clean(cfg.handle);
    return '';
  }

  // ── funnel ────────────────────────────────────────────────────────────────
  /** The stage this pageview alone demonstrates. Cart and checkout are events,
   *  not page types, so they are recorded by their own handlers. */
  function stageForPage() {
    if (cfg.pageType === 'product') return 'product';
    if (cfg.pageType === 'collection' || cfg.pageType === 'blog' || cfg.pageType === 'article') {
      return 'browse';
    }
    return 'land';
  }

  /**
   * Advance the funnel to `stage`, recording the day it was first reached.
   * Returns true when this was genuinely new, so callers can skip a re-stamp.
   */
  function reachStage(store, stage) {
    var rank = STAGES.indexOf(stage);
    if (rank < 0) return false;
    if (!store.funnel) store.funnel = {};
    if (store.funnel[stage]) return false; // already been here

    // Only record a forward move. Without this, every later pageview would
    // re-add 'browse' behind a shopper who is already at checkout.
    var best = -1;
    for (var s in store.funnel) {
      if (Object.prototype.hasOwnProperty.call(store.funnel, s)) {
        best = Math.max(best, STAGES.indexOf(s));
      }
    }
    if (rank <= best) return false;

    var d = new Date();
    store.funnel[stage] =
      String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    return true;
  }

  function formatFunnel(store) {
    if (!store.funnel) return '';
    return STAGES.filter(function (s) {
      return store.funnel[s];
    })
      .map(function (s) {
        return s + ':' + store.funnel[s];
      })
      .join('>')
      .slice(0, FIELD_MAX);
  }

  // ── state ─────────────────────────────────────────────────────────────────
  function updateState() {
    var store = readStore() || { firstAt: Date.now(), first: null, last: null, path: [] };
    var touch = currentTouch();

    if (touch) {
      if (!store.first) store.first = touch;
      store.last = touch; // only advances on an identifiable touch
    }

    var internal = internalTouch();
    if (internal && store.path[store.path.length - 1] !== internal) {
      store.path.push(internal);
      if (store.path.length > PATH_MAX) store.path = store.path.slice(-PATH_MAX);
    }

    // A visitor who arrived with no campaign but read an article still needs a
    // first touch, otherwise the content trail has nothing to hang off.
    if (!store.first && internal) {
      store.first = { source: 'direct', medium: 'none', campaign: '', content: '', date: today() };
    }

    reachStage(store, stageForPage());

    // A visitor arriving straight onto a product page has plainly landed and
    // browsed too; without this the funnel would show a product view with no
    // steps before it and the drop-off rates would read as nonsense.
    if (!store.funnel.land) store.funnel.land = store.funnel[stageForPage()];

    writeStore(store);
    return store;
  }

  function format(touch) {
    if (!touch) return '';
    return [touch.source, touch.medium, touch.campaign, touch.content, touch.date].join('|');
  }

  function attributesFor(store) {
    var attrs = {};
    if (store.first) attrs._up_first = format(store.first);
    if (store.last) attrs._up_last = format(store.last);
    if (store.path.length) attrs._up_path = store.path.join('>').slice(0, 250);
    var funnel = formatFunnel(store);
    if (funnel) attrs._up_funnel = funnel;

    // Cognify campaign id, under the name the API actually looks for.
    //
    // WHY: the order webhook reads `cognify_campaign_id` off the order and
    // `_cognify_campaign_id` off each line item. This file only ever wrote
    // `_up_*`, so the two never met and every order arrived unattributed —
    // measured: the webhook delivered fine, the conversions table stayed at 0.
    //
    // No underscore prefix here: Shopify hides underscore-prefixed attributes
    // from the customer, but the webhook reads the order-level attribute by its
    // plain name, so this one has to stay visible to be readable at all.
    try {
      var cid =
        (window.cognifyAnalytics && window.cognifyAnalytics.campaignId) || '';
      if (cid) attrs.cognify_campaign_id = String(cid).slice(0, 64);
    } catch (e) {
      /* config absent — nothing to attribute, order still records as unattributed */
    }

    return attrs;
  }

  // ── stamping the cart ─────────────────────────────────────────────────────
  var stamping = false;

  function stamp(store) {
    if (stamping) return;

    var attrs = attributesFor(store);
    var keys = Object.keys(attrs);
    if (!keys.length) return;

    // Skip the request when nothing changed since the last stamp this session.
    var signature = keys.map(function (k) { return k + '=' + attrs[k]; }).join('&');
    try {
      if (window.sessionStorage.getItem(SENT_KEY) === signature) return;
    } catch (e) {
      /* ignore — worst case we re-send */
    }

    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    stamping = true;

    window
      .fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      })
      .then(function (res) {
        if (!res.ok) return;
        try {
          window.sessionStorage.setItem(SENT_KEY, signature);
        } catch (e) {
          /* ignore */
        }
      })
      .catch(function () {
        /* never surface an attribution failure to the shopper */
      })
      .finally(function () {
        stamping = false;
      });
  }

  /**
   * Stamp during navigation. `fetch` is cancelled the moment the browser leaves
   * the page, which is precisely when the checkout stage happens — so that one
   * stage goes out via sendBeacon, which the browser is obliged to deliver.
   * Falls back to the normal path where sendBeacon is unavailable.
   */
  function stampBeacon(store) {
    var attrs = attributesFor(store);
    if (!Object.keys(attrs).length) return;

    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var body = JSON.stringify({ attributes: attrs });

    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }
    } catch (e) {
      /* fall through */
    }
    stamp(store);
  }

  // ── boot ──────────────────────────────────────────────────────────────────
  // A consent banner that has not been answered yet parks the whole module and
  // arms a listener; nothing is recorded until the shopper decides.
  if (!consentOk()) return;

  if (!consentApiReady() && document.documentElement.hasAttribute('data-shopify-privacy-banner')) {
    document.addEventListener('visitorConsentCollected', function onConsent() {
      document.removeEventListener('visitorConsentCollected', onConsent);
      if (consentOk()) boot();
    });
    return;
  }

  boot();

  function boot() {
  var state = updateState();

  // Already holding something? Stamp now — this covers returning visitors and
  // anyone who lands mid-session with a cart from a previous visit.
  if (Number(cfg.itemCount) > 0) stamp(state);

  // Otherwise wait for the first add. Patching fetch rather than listening for
  // the theme's pub/sub keeps this working across every add-to-cart path the
  // theme has (product form, quick add, bulk add, drawer) without coupling to
  // any of them.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var result = nativeFetch.apply(this, arguments);
    try {
      var url = typeof input === 'string' ? input : input && input.url;
      if (url && url.indexOf('/cart/add') !== -1) {
        result
          .then(function (res) {
            if (!res || !res.ok) return;
            var s = readStore() || state;
            if (reachStage(s, 'cart')) writeStore(s);
            stamp(s);
          })
          .catch(function () {});
      }
    } catch (e) {
      /* ignore */
    }
    return result;
  };

  // ── checkout intent ───────────────────────────────────────────────────────
  // Capture phase and both event types on purpose: the theme reaches checkout
  // from a cart-page form submit, from the drawer's checkout button, and from
  // Shopify's dynamic wallet buttons, which are inside an iframe we cannot see.
  // Anything that is a checkout submit button, or a link into /checkout, counts.
  function isCheckoutIntent(el) {
    if (!el || !el.closest) return false;
    if (el.closest('[name="checkout"], [href*="/checkout"], .shopify-payment-button')) return true;
    var form = el.closest('form[action*="/cart"]');
    return !!(form && el.closest('button[type="submit"][name="checkout"]'));
  }

  function onCheckoutIntent(event) {
    try {
      var target = event.target;
      var hit =
        event.type === 'submit'
          ? target && target.getAttribute && /\/checkout/.test(target.getAttribute('action') || '')
          : isCheckoutIntent(target);
      if (!hit && event.type === 'submit') {
        // The cart form posts to /cart with a checkout-named submitter.
        hit = !!(event.submitter && event.submitter.name === 'checkout');
      }
      if (!hit) return;

      var s = readStore() || state;
      if (reachStage(s, 'cart')) writeStore(s); // reaching checkout implies a cart
      if (reachStage(s, 'checkout')) {
        writeStore(s);
        stampBeacon(s);
      }
    } catch (e) {
      /* never interfere with checkout */
    }
  }

  document.addEventListener('click', onCheckoutIntent, true);
  document.addEventListener('submit', onCheckoutIntent, true);
  }
})();
